import { useMemo } from 'react';

import MonthGrid from '../ui/MonthGrid';
import {
  SEQUENTIAL,
  SEQUENTIAL_LABELS,
  formatDayLong,
  formatMonth,
  sequentialStep,
} from '../../config/chartTheme';
import type { TimeRow } from './timeRows';

/**
 * Belegung je Tag als Monatsraster.
 *
 * Zweite Lesart neben der Zeitreihe: die Linie zeigt den Verlauf, das Raster
 * zeigt einzelne Tage. Für Feiertagsblöcke ist das Raster die stärkere Form —
 * die Silvesterwoche erscheint hier als zusammenhängender dunkler Block, in der
 * Linie nur als schmale Zacke.
 */
export default function OccupancyCalendar({ rows }: { rows: TimeRow[] }) {
  const months = useMemo(() => groupByMonth(rows), [rows]);
  if (months.length === 0) return null;

  return (
    <div>
      <Legend />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-3">
        {months.map(({ year, monthIdx, days }) => (
          <MonthGrid
            key={`${year}-${monthIdx}`}
            year={year}
            monthIdx={monthIdx}
            label={formatMonth(`${year}-${String(monthIdx + 1).padStart(2, '0')}`)}
            renderDay={({ iso }) => {
              const row = days.get(iso);
              const share = row?.observedPct ?? row?.announcedPct ?? null;
              const color = sequentialStep(share == null ? null : share / 100);

              if (color == null) {
                // Keine Daten. Bewusst strukturell abgesetzt statt farblich:
                // die hellste Rampenstufe steht bei 0 % dicht am Weiß, ein
                // blasser Farbunterschied wäre nicht wahrnehmbar.
                return {
                  className: 'border border-dashed border-gray-300',
                  title: row ? `${formatDayLong(iso)}: keine Angabe` : `${iso}: außerhalb des Zeitraums`,
                };
              }
              return {
                style: { backgroundColor: color },
                title: `${formatDayLong(iso)}: ${String(share).replace('.', ',')} % belegt · ${row?.n ?? 0} Inserate`,
              };
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500">
      <div className="flex items-center gap-1.5">
        <span>Belegung</span>
        <div className="flex gap-px">
          {SEQUENTIAL.map((color, i) => (
            <span
              key={color}
              className="w-6 h-3 rounded-sm"
              style={{ backgroundColor: color }}
              title={SEQUENTIAL_LABELS[i]}
            />
          ))}
        </div>
        <span className="text-gray-400">0 → 100 %</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm border border-dashed border-gray-300" />
        <span className="text-gray-400">keine Angabe</span>
      </div>
    </div>
  );
}

interface MonthBucket {
  year: number;
  monthIdx: number;
  days: Map<string, TimeRow>;
}

function groupByMonth(rows: TimeRow[]): MonthBucket[] {
  const buckets = new Map<string, MonthBucket>();
  for (const row of rows) {
    const key = row.iso.slice(0, 7);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        year: Number(row.iso.slice(0, 4)),
        monthIdx: Number(row.iso.slice(5, 7)) - 1,
        days: new Map(),
      };
      buckets.set(key, bucket);
    }
    bucket.days.set(row.iso, row);
  }
  return [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
}
